<script setup lang="ts">
import { modelIconForRow } from "@/composables/modelIcons";
import EditableSearchTableHeader from '@/components/DataTable/EditableSearchTableHeader.vue';
import SearchMapView from '@/components/SearchMapView.vue';
import { buildSearchFilterPayload, useSearchApi } from '@/composables/api/SearchApi';
import TrackListExportControls from '@/components/TrackListExportControls.vue';
import SyncStatusButton from '@/components/Sync/SyncStatusButton.vue';
import { showsSyncPill } from '@/utils/syncStatus';
import type { TrackListExportSource } from '@/types/trackListExport';
import { storeToRefs } from 'pinia';
import { computed, nextTick, provide, reactive, ref, toRaw, watch } from 'vue';
import { requestPlaylistContainerSection } from '@/composables/playlistContainerSectionRequest';
import { useEntityStore } from '@/stores/entityStore';
import { useRoutedSavedView } from '@/composables/useRoutedSavedView';
import { useRoute as useListRoute, useRouter as useListRouter } from 'vue-router';
import { isSelectionClick } from '@/components/DataTable/rowClick';
import { listSearchRule, listSearchText, withListSearch } from '@/utils/listSearch';
import { hiddenMatch, provideQuickSearchMatches } from '@/composables/quickSearchMatches';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { recordListState } from '@/composables/useRecordListContext';
import { areaEntity, recordPathFor } from '@/navigation/areas';
import DataTable from '@/components/DataTable/DataTable.vue';
import SearchCardsSplitView from '@/components/DataTable/SearchCardsSplitView.vue';
import RecordPane from '@/components/record/RecordPane.vue';
import ListRowPaneScope from '@/components/DataTable/ListRowPaneScope.vue';
import { createPaneTrail, LIST_PANE_HOST } from '@/composables/recordPaneTrail';
import { entityDisplayNameFor } from '@/composables/entityColumns';
import TableRecordSplitView from '@/components/DataTable/TableRecordSplitView.vue';
import { listMayHandleKey, tableSelectionKeyAction } from '@/components/DataTable/tableSelectionKeys';
import SearchResultListRow from '@/components/DataTable/SearchResultListRow.vue';
import ListGroupHeader from '@/components/DataTable/ListGroupHeader.vue';
import { groupHeadersFor } from '@/components/DataTable/groupBoundaries';
import {
  cardFixedColumnIdsFor,
  fixedCardHeaderComponentFor,
  cardShownMatchFields,
  isPlaylistContainerEntity,
  inspectorExcludeFieldsFor,
} from '@/composables/searchInspectorRegistry';
import LocationZonePlayerCardHeader from '@/components/DataTable/LocationZonePlayerCardHeader.vue';
import PlaylistContainerCardHeader from '@/components/DataTable/PlaylistContainerCardHeader.vue';
import CardTags from '@/components/CardTags.vue';
import LzpBulkIssueBar from '@/components/DataTable/LzpBulkIssueBar.vue';
import SearchLivePollingIndicator from '@/components/DataTable/SearchLivePollingIndicator.vue';
import { useSearchResultsLivePolling } from '@/composables/api/useSearchResultsLivePolling';
import ColumnMapping from '@/components/ColumnMapping.vue';
import PlayTrackButton from '@/components/PlayTrackButton.vue';
import CoverArtTile from '@/components/CoverArtTile.vue';
import LzpCurrentlyPlayingCard from '@/components/DataTable/LzpCurrentlyPlayingCard.vue';
import { useCurrentTrackSummaries } from '@/composables/api/useCurrentTrackSummaries';
import { useInspectorStore } from '@/stores/inspectorStore';
import { useInspectorTopOffset } from '@/composables/useInspectorTopOffset';
import { useEventListener, useMediaQuery } from '@vueuse/core';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { useCursorPager } from '@/composables/useCursorPager';
import { toBulkSelectionId, useBulkSelection } from '@/composables/useBulkSelection';
import BulkActionBar from '@/components/BulkActions/BulkActionBar.vue';
import type { BulkBarScheduleSelection } from '@/components/BulkActions/bulkSelectionText';
import type { MixedRunKind } from '@/composables/useMixedBulkRun';
import { useMatchingScheduleCount } from '@/composables/useMatchingScheduleCount';
import { cardSchedulesFor, cardSchedulesKeyOf } from '@/components/schedules/useCardSchedules';
import type { ScheduleEntry } from '@/components/schedules/scheduleGroups';
import { provideCardSchedules } from '@/components/schedules/cardSchedulesContext';
import TableScheduleSortScope from '@/components/schedules/TableScheduleSortScope.vue';
import { usePermissions } from '@/composables/usePermissions';
import { getSearchTableCustomColumns } from '@/composables/searchTableCustomColumns';
import { exportColumnOptionsFor, isExportedByDefault } from '@/composables/exportColumns';
import { readNestedId } from '@/utils/nestedRelation';
import { provideTrackQueueContext } from '@/composables/trackQueueContext';
import type { TableColumn } from '@/types/tableTypes';

const entityStore = useEntityStore();
const { entitySlug, selectedEntity, selectedEntityId, entities } = storeToRefs(entityStore);

/**
 * Zero permitted models (SAU-287): the permission filter left nothing to list.
 * Rendered as its own panel instead of a dead table.
 */
const hasNoPermittedEntities = computed(() => entities.value.length === 0);

/** Per-entity non-model columns (currently just Play for LiveTrack / RawDdexTrack). */
const searchCustomColumns = computed(() => getSearchTableCustomColumns(selectedEntityId.value));

/** <PlayTrackButton> props for a search row, by the selected entity. */
function playButtonPropsForRow(row: Record<string, unknown>) {
  const title = (row.name as string) ?? (row.title as string) ?? null;
  const coverUrl =
    (row.cover_art_signed_url as string) ?? (row.cover_art_url as string) ?? null;
  // LiveTrack search docs nest artist/album as {id, display_string};
  // RawDdexTrack stores `album_name` (string) and `artists` (array). Try the
  // nested shape first and fall back to the raw columns so the player gets
  // the right strings regardless of which entity drives the search.
  const artist = readNestedDisplay(row.artist) ?? readArtistsArray(row.artists);
  const album =
    readNestedDisplay(row.album) ?? ((row.album_name as string | null | undefined) ?? null);

  if (selectedEntityId.value === 'RawDdexTrack') {
    return {
      kind: 'raw_ddex' as const,
      rawDdexTrackId: (row.id as number | string) ?? null,
      hasAudio: Boolean(row.audio_file_s3_path),
      title,
      artist,
      album,
      coverUrl,
    };
  }
  // LiveTrack
  return {
    kind: 'live_track' as const,
    liveTrackCode: (row.code as string) ?? null,
    title,
    artist,
    album,
    coverUrl,
    // Records for the player bar's identity lines. Only the nested
    // {id, display_string} shape carries ids; a row that fell back to a plain
    // `album_name` string has none, and that line stays plain text.
    liveTrackId: (row.id as number | string) ?? null,
    artistId: readNestedId(row.artist),
    albumId: readNestedId(row.album),
  };
}

function readNestedDisplay(value: unknown): string | null {
  if (value && typeof value === 'object' && 'display_string' in value) {
    const ds = (value as { display_string?: unknown }).display_string;
    return typeof ds === 'string' && ds.trim() !== '' ? ds : null;
  }
  return null;
}

function readArtistsArray(value: unknown): string | null {
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (first && typeof first === 'object' && 'name' in first) {
      const name = (first as { name?: unknown }).name;
      return typeof name === 'string' && name.trim() !== '' ? name : null;
    }
  }
  return null;
}

const MAP_ENTITY_IDS = ['Account', 'Location', 'LocationZone'] as const;
const canUseMapView = computed(() =>
  MAP_ENTITY_IDS.includes(selectedEntityId.value as (typeof MAP_ENTITY_IDS)[number]),
);

// `/players?view=12` opens view 12, and picking a view updates the address.
// Only a list route names its entity; tests that mount the page bare do not.
const listRoute = useListRoute();
const onEntityListRoute = typeof listRoute?.meta?.entityId === 'string';
const routedView = onEntityListRoute ? useRoutedSavedView() : null;
const listRouter = onEntityListRoute ? useListRouter() : null;

/**
 * Open a row on its own page. The rows on screen go with it in the history
 * state, so the record page can show "1 of 20" and step through them, and its
 * breadcrumb names this list: the saved view if one is open, else the entity.
 */
function openRecordPage(row: Record<string, unknown>) {
  const recordId = Number(row.id);
  if (!listRouter || !Number.isInteger(recordId)) return;
  const ids = displayRows.value.map((candidate: Record<string, unknown>) => Number(candidate.id)).filter(Number.isInteger);
  const label = routedView?.activeView.value?.name ?? areaEntity(selectedEntityId.value)?.label ?? selectedEntityId.value;
  void listRouter.push({
    path: recordPathFor(selectedEntityId.value, recordId),
    state: recordListState({ ids, label, path: listRoute.fullPath }),
  });
}

const { openRecord } = useOpenRecordInInspector();


/**
 * A plain click on a table row shows the record in the pane below the table. A
 * phone has no pane and opens the full page. Cmd, Ctrl or Shift ticks rows
 * instead, where rows can be ticked. There is no setting for it.
 * Spec: docs/specs/record-navigation-layers.md, "The list's pane".
 */
function onTableRowClick(row: Record<string, unknown>, event: MouseEvent) {
  if (isSelectionClick(event)) {
    if (isBulkSelectable.value) onToggleBulkSelect(row, event);
    return;
  }
  if (!isTablePaneActive.value) {
    openRecordPage(row);
    return;
  }
  const scheduleListId = scheduleListIdFor(row);
  if (scheduleListId !== null) requestScheduleInList(row, scheduleListId);
  selectedRowId.value = String(row.id);
  // Clicking the row already selected also starts the pane fresh on it.
  paneTrail.clear();
}

const {
  columns,
  pinnedColumns,
  unpinnedColumns,
  visiblePinnedColumns,
  visibleNonPinnedColumns,
  cardVisibleColumns,
  // The sort the server gets: the user's sort, with the Group by column first.
  effectiveSort: columnSort,
  groupBy,
  compositeFilter,
  pageIndex,
  pageSize,
  toggleSort,
  setColumnSize,
  updateColumnState,
  searchViewMode,
  tableLayout,
  filter: tableFilter,
  setFilter: setTableFilter,
} = usePersistentTable({ additionalColumns: searchCustomColumns });

// `/players?q=rau` arrives from the search page's "Open in list". The text
// becomes the list's search, exactly as if typed into its search box, and
// leaves the address, since the filter now holds it.
if (onEntityListRoute && typeof listRoute.query.q === 'string' && listRoute.query.q.trim() !== '') {
  const searchRule = listSearchRule(listRoute.query.q.trim());
  setTableFilter(withListSearch(tableFilter.value, searchRule));
  const restOfQuery = { ...listRoute.query };
  delete restOfQuery.q;
  void listRouter?.replace({ query: restOfQuery });
}

const effectiveViewMode = computed(() => {
  if (canUseMapView.value && searchViewMode.value === 'map') {
    return 'map';
  }
  return tableLayout.value === 'cards' || tableLayout.value === 'fixed-cards'
    ? 'cards'
    : 'list';
});

/**
 * The result set on screen, as the `source` node the export and play-queue
 * endpoints share: which entity, the current filter, the current sort.
 *
 * Every searchable entity, not just Live Tracks. The filter goes through the
 * same cleaning the search request gets, so a half-built rule the results
 * ignore is ignored here too — sending the raw builder state would name a
 * different list from the one on screen.
 *
 * Null only while no list entity has resolved, which is the
 * zero-grant case the page already renders its own panel for.
 */
const searchSource = computed<TrackListExportSource | null>(() =>
  entitySlug.value
    ? {
        type: 'search',
        entity: entitySlug.value,
        filter: buildSearchFilterPayload(compositeFilter.value),
        sort: columnSort.value,
      }
    : null,
);

/** "Live Track search", "Account search" — matching what the backend names it. */
const searchLabel = computed(
  () => `${selectedEntity.value?.entityDisplayName ?? 'Search'} search`,
);

/**
 * Play-in-context for search results: clicking Play on a row queues the whole
 * result set behind the current filter and sort. Read as a getter at click
 * time so a filter change between mount and click queues the list the user is
 * actually looking at.
 *
 * Live Tracks ONLY, unlike the export above. A queue entry is an audio file
 * plus the fields a track card renders, so there is nothing to play in an
 * Account search — the endpoint refuses one, and a RawDdexTrack row falls back
 * to playing exactly one track.
 */
provideTrackQueueContext(() =>
  selectedEntityId.value === 'LiveTrack' && searchSource.value
    ? {
        source: searchSource.value,
        label: searchLabel.value,
        // The pager's last known total. It is 0 until a response carries one and
        // is reset to 0 on any filter/sort change, so it can only ever say "1"
        // about the result set on screen — and a result set of one has nothing
        // behind the clicked row to fetch.
        knownTotal: cachedTotal.value,
      }
    : null,
);

/**
 * The columns on screen, in their order, on the layout the user is looking at.
 * The export column list starts with these ticked until the user has exported
 * this record type once. After that it starts from their last export choice.
 * The table reads its own Column Settings
 * (pinned first, as rendered). The card layouts read the card set — the
 * always-on header fields first, then the opt-in summary columns — which is
 * the order Column Settings lists them in for cards.
 *
 * Custom display columns (Play) are left out: they are buttons, not data.
 * A login password starts unticked, so the workbook carries it only when the
 * person ticks it. Nothing else is filtered here. Which columns a spreadsheet can carry is the
 * exporter's rule, and this page does not keep a copy of it — it sends what
 * the user is looking at and the endpoint says what it took and what it left.
 */
const exportColumnIds = computed<string[]>(() => {
  const dataColumnIds = (exportColumns: TableColumn[]): string[] =>
    exportColumns.filter(isExportedByDefault).map((column) => column.id);

  // The plain Cards layout is the ONLY one whose card column picker is the
  // live control, so it is the only one whose card set describes what the user
  // chose. Map view and the Fixed Card layout also render this toolbar, and
  // both used to fall in here: Accounts on the map exported `id` and `name`
  // while every pin card on screen showed the address, and the Fixed Card
  // exported two columns for a card showing thirteen — with Column Settings
  // hidden in that layout, so there was nothing the user could do about it.
  //
  // Everywhere else the table's Column Settings is the last column choice the
  // user actually made, so that is what the workbook carries.
  if (effectiveViewMode.value === 'cards' && tableLayout.value === 'cards') {
    return [...cardFixedColumnIdsFor(selectedEntityId.value), ...dataColumnIds(cardColumns.value)];
  }

  return dataColumnIds([...visiblePinnedColumns.value, ...visibleNonPinnedColumns.value]);
});

/**
 * Every column the export column list offers on this page, with its name.
 * The columns above only decide which ones start ticked.
 */
const exportColumnOptions = computed(() => exportColumnOptionsFor(columns.value));

/**
 * Compact cover thumb for the REGULAR cards view (the fixed card brings its
 * own full-size cover): entities whose rows carry playable cover art get a
 * small leading tile — the same play-overlay + expand-to-zoom cover chain,
 * sized to the header row plus one summary line.
 */
const showCoverThumb = computed(
  () => selectedEntityId.value === 'LiveTrack' || selectedEntityId.value === 'RawDdexTrack',
);

/**
 * The generic card header's leading mark. Container entities never reach it —
 * they supply their own header — so this answers for everything else, and a
 * model with no per-record variant just gets its model mark.
 */
function titleIconForRow(row: Record<string, unknown>) {
  return modelIconForRow(selectedEntityId.value, row);
}

function coverThumbAlt(row: Record<string, unknown>): string {
  const title = (row.name as string) ?? (row.title as string) ?? `#${row.id}`;
  return `Cover art for ${title}`;
}

/**
 * Card tag chips render when the fixed card is active (tags are part of the
 * fixed card) or when the user's Column Settings include the Tags column —
 * hiding the column hides the chips. The text summary never spells tags out
 * (the chips are their representation), hence the row-level exclude below.
 */
const showCardTags = computed(
  () =>
    fixedCardComponent.value !== null ||
    cardColumns.value.some((column) => column.id === 'tags'),
);

/**
 * The rating chip (and the loud red NR) is not a tag — it must survive the
 * Tags column being toggled off. The footer renders when either should show;
 * the Tags toggle only controls the tag chips themselves.
 */
const showLiveTrackRating = computed(() => selectedEntityId.value === 'LiveTrack');

const rowExcludeFields = computed(() => [...cardExcludeFields.value, 'tags']);

/**
 * The entity's fixed card component while the Fixed Card layout is active —
 * rows render it INSTEAD of the dynamic column summary. Null in every other
 * layout (and for entities without a fixed card, where the option is not
 * offered and a persisted preference falls back to plain cards).
 */
const fixedCardComponent = computed(() =>
  tableLayout.value === 'fixed-cards'
    ? fixedCardHeaderComponentFor(selectedEntityId.value)
    : null,
);

/**
 * A bucket or a custom list gets the two-line container header instead of the
 * generic title row. It must stay in step with `cardFixedColumnIdsFor`, which
 * pins the fields that header draws: pin without drawing and the zone, the
 * location and the parent bucket leave the summary line with nothing showing
 * them and no checkbox left to bring them back.
 */
const isContainerCard = computed(() => isPlaylistContainerEntity(selectedEntityId.value));

/**
 * Master-list cards render the CARD-visible columns (opt-in, defaults-only
 * until the user turns more on — see cardVisibleColumns); the table's own
 * column visibility is untouched. Custom display columns (play,
 * currently_playing) render as rich blocks, not summary text.
 *
 * Card-fixed columns are excluded: the card header always shows them, and
 * Column Settings pins them checked-and-disabled in card layouts — a
 * previously persisted visibility entry (e.g. Account's Name before it was
 * pinned) would otherwise print the value a second time in the summary with
 * no toggle left to turn it off.
 */
const cardColumns = computed(() => {
  const fixedColumnIds = new Set(cardFixedColumnIdsFor(selectedEntityId.value));
  return cardVisibleColumns.value.filter(
    (column) => column.dataType !== 'display' && !fixedColumnIds.has(column.id),
  );
});

/** The LZP "Currently Playing" block — a card-visible custom column, on by default. */
const currentlyPlayingVisible = computed(
  () =>
    isLocationZonePlayer.value &&
    cardVisibleColumns.value.some((column) => column.id === 'currently_playing'),
);

/** The inspector always shows every field, grouped — independent of Column Settings. */
const inspectorColumns = computed(() => columns.value);

/**
 * Fields the card header already shows. For Location Zone Player the header is
 * fixed (status light, id, location, zone, time since check-in) and always
 * visible regardless of Column Settings; everything else comes and goes with
 * column visibility. Shared with every inspector surface via the registry.
 */
const cardExcludeFields = computed(() => inspectorExcludeFieldsFor(selectedEntityId.value));


/** Once true, keep `SearchMapView` mounted and toggle with `v-show` so MapKit is not torn down (blank map on return). */
const mapHostReady = ref(false);

watch(
  () => [canUseMapView.value, effectiveViewMode.value] as const,
  ([canMap, mode]) => {
    if (!canMap) {
      mapHostReady.value = false;
      return;
    }
    if (mode === 'map') {
      mapHostReady.value = true;
    }
  },
  { immediate: true, flush: 'sync' },
);

// Cursor (search_after) paging — any page (incl. Last) without tripping the 10k window (TABASCO-1HT).
const { cursor, pageNumber, cachedTotal, hasNext, hasPrev, reset: resetPager, captureMeta, navigate: navigateHandler } =
  useCursorPager(pageSize);

// Register this reset BEFORE useSearchApi so an entity/sort/filter/page-size change nulls the cursor
// before vue-query's options watcher (registered inside the composable) reacts — otherwise the first
// refetch goes out with the previous page's token under the new query (stale request + double fetch).
watch(
  () => JSON.stringify([entitySlug.value, columnSort.value, compositeFilter.value, pageSize.value]),
  () => resetPager(),
);

const {
  data,
  isLoading,
  isError: isSearchError,
} = useSearchApi({
  entitySlug,
  cursor,
  pageSize,
  sorting: columnSort,
  filter: compositeFilter
});
watch(data, () => captureMeta(data.value?.meta));

// Quick search (SAU-467): which fields each row matched, for the cells and cards to
// highlight, and the one extra line under a row whose matched field is not on screen.
const quickSearchMatches = provideQuickSearchMatches({
  entityId: () => selectedEntityId.value,
  text: () => listSearchText(tableFilter.value),
  meta: () => data.value?.meta,
});
const tableShownColumns = computed(() =>
  [...visiblePinnedColumns.value, ...visibleNonPinnedColumns.value].filter((column) => column.id !== 'play' && !column.cardOnly),
);
const tableShownFieldKeys = computed(() => new Set(tableShownColumns.value.map((column) => column.key)));
/** The extra line goes under the row's first column, so it reads as part of the row. */
const firstTableColumnId = computed(() => tableShownColumns.value[0]?.id ?? null);
function cellMatched(row: Record<string, unknown>, columnKey: string): boolean {
  return quickSearchMatches.matchesFor(row.id).some((match) => match.field === columnKey);
}
function tableHiddenMatch(row: Record<string, unknown>) {
  return hiddenMatch(quickSearchMatches.matchesFor(row.id), tableShownFieldKeys.value);
}
/** Shared by every row with no match, so the prop stays the same array between redraws. */
const NO_MATCHED_FIELDS: string[] = [];
/**
 * Each row's matched fields, built again only when the rows or the matches change. A
 * card's header and slots get the same array on every page redraw, so selecting a card
 * does not redraw them all.
 */
const cardMatchedFieldsById = computed(() => {
  const fieldsById = new Map<string, string[]>();
  for (const row of displayRows.value) {
    const matches = quickSearchMatches.matchesFor(row.id);
    if (matches.length > 0) {
      fieldsById.set(String(row.id), matches.map((match) => match.field));
    }
  }
  return fieldsById;
});
/**
 * The fields this card row matched, for its header and slots to highlight. The page
 * passes them in, and the components never read the matches by row id themselves,
 * because they are drawn elsewhere for other records too.
 */
function cardMatchedFields(row: Record<string, unknown>): string[] {
  return cardMatchedFieldsById.value.get(String(row.id)) ?? NO_MATCHED_FIELDS;
}

// Live polling: re-fetch the visible rows by id on the inspector's cadence and
// merge fresh values in place (size-gated). `displayRows` is what every view
// renders — same rows in the same order as `data`, just with live cell values.
const {
  state: livePollState,
  payloadBytes: livePollPayloadBytes,
  maxPayloadBytes: livePollMaxPayloadBytes,
  pollIntervalMs: livePollIntervalMs,
  lastUpdatedAt: livePollLastUpdatedAt,
  displayRows,
  isRowMissing,
} = useSearchResultsLivePolling({
  entitySlug,
  searchData: data,
  // Bridge open-inspector record polls into the row list (keeps the detail
  // pane and its row in lockstep — the inspector polls faster).
  recordEntityId: selectedEntityId,
});

/**
 * The group headers for the card lists. The table works out its own from the same
 * helper. The cards look the column up among all columns, because the group column
 * need not be one of the fields a card shows.
 */
const cardGroupHeaders = computed(() => groupHeadersFor(displayRows.value, groupBy.value, columns.value));

// Batched now-playing lookup for the LZP cards/cells (empty rows = no fetch).
const currentTrackRows = computed(() =>
  selectedEntityId.value === 'LocationZonePlayer' ? displayRows.value : [],
);
const { summaryForRow: currentTrackSummaryForRow } = useCurrentTrackSummaries(currentTrackRows);

/**
 * The matched fields the card's header and slots draw for this row, so the card adds
 * no extra line for them. The flags are the ones the card template below uses to decide
 * what it draws.
 */
function cardShownFieldKeys(row: Record<string, unknown>): string[] {
  if (quickSearchMatches.matchesFor(row.id).length === 0) {
    return [];
  }
  return cardShownMatchFields(selectedEntityId.value, row, {
    fixedCard: fixedCardComponent.value !== null,
    tags: showCardTags.value,
    rating: showLiveTrackRating.value,
    currentlyPlaying: currentlyPlayingVisible.value,
    currentTrackResolved: currentlyPlayingVisible.value && currentTrackSummaryForRow(row) !== null,
  });
}

// --- Cards master-detail (Apple Mail style) ---
const inspectorStore = useInspectorStore();

// Keep the side-peek inspector below the toolbar row so the list search,
// filters, and paginator stay visible and clickable while it is open.
const toolbarRef = ref<HTMLElement | null>(null);
useInspectorTopOffset(toolbarRef);
const isDesktop = useMediaQuery('(min-width: 768px)');
const isSplitViewActive = computed(() => effectiveViewMode.value === 'cards' && isDesktop.value);
/** The table shows its selected record below it: a desktop table on a list route. */
const isTablePaneActive = computed(
  () => onEntityListRoute && effectiveViewMode.value === 'list' && isDesktop.value,
);

const selectedRowId = ref<string | null>(null);
// Resolve the selection against displayRows so the inline detail pane opens
// with the freshest polled values (same ids/order as the raw search rows).
const selectedRow = computed(
  () =>
    displayRows.value.find(
      (row: Record<string, unknown>) => String(row.id) === selectedRowId.value,
    ) ?? null,
);

const CUSTOM_LIST_ENTITY_ID = 'LocationZoneCustomPlaylist';

/**
 * The custom list a schedule row opens. A schedule is always read in its list, so a click on a
 * schedule opens the list's inspector on Schedules, with that schedule highlighted, rather than
 * a page of the schedule's own columns. Null for any other entity.
 */
function scheduleListIdFor(row: Record<string, unknown> | null): number | null {
  if (selectedEntityId.value !== 'LocationZoneCustomPlaylistSchedule' || row === null) return null;
  const listId = Number(row.location_zone_custom_playlist_id);
  return Number.isInteger(listId) && listId > 0 ? listId : null;
}

/** Ask the list's inspector to open on Schedules and point at this schedule. */
function requestScheduleInList(row: Record<string, unknown>, listId: number): void {
  requestPlaylistContainerSection(CUSTOM_LIST_ENTITY_ID, listId, 'schedules', row.id);
}

/** The selected schedule's list, when the split view shows a list for a schedule. */
const selectedScheduleListId = computed(() => scheduleListIdFor(selectedRow.value));

/**
 * The pane's trail: the records a related link moved the pane to, after the
 * selected row. Selecting another row starts it fresh. `sync`, so a row
 * selected by a link in that row is cleared before the link's record is added.
 * Spec: docs/specs/record-navigation-layers.md, "The pane's trail".
 */
const paneTrail = createPaneTrail();
watch(selectedRowId, (rowId) => paneTrail.startFreshFor(rowId), { flush: 'sync' });

/** Whether the list shows its record in a pane: a desktop table, or desktop cards. */
const hasRecordPane = computed(() => isTablePaneActive.value || isSplitViewActive.value);

/**
 * A related record's link in a table cell or on a card selects its row, and
 * moves the pane on from that row to the record. A link to the row's own
 * record, such as the ID cell's peek button, only selects the row.
 */
provide(LIST_PANE_HOST, {
  isActive: () => hasRecordPane.value,
  showFromRow(rowId, entityId, recordId, options) {
    const row = (displayRows.value as Record<string, unknown>[]).find((candidate) => String(candidate.id) === rowId);
    if (!row) return;
    selectRowForPane(row);
    paneTrail.clear();
    if (entityId === selectedEntityId.value && String(recordId) === rowId) return;
    paneTrail.push({
      entityId,
      recordId,
      title: `${entityDisplayNameFor(entityId)} #${recordId}`,
      ...(options?.initialSection ? { initialSection: options.initialSection } : {}),
    });
  },
});

/** Selects a row so the pane shows it, as a plain click on the row does. */
function selectRowForPane(row: Record<string, unknown>) {
  const id = String(row.id);
  if (isLocationZonePlayer.value) {
    selectedPlayerIds.value = new Set([id]);
    rangeAnchorId.value = id;
  }
  const scheduleListId = scheduleListIdFor(row);
  if (scheduleListId !== null) requestScheduleInList(row, scheduleListId);
  selectedRowId.value = id;
}

/** Drop the selection when the visible rows change so we never highlight a row that's no longer rendered. */
watch(
  () => data.value?.data ?? [],
  (rows: Record<string, unknown>[]) => {
    if (selectedRowId.value === null) return;
    if (!rows.some((row) => String(row.id) === selectedRowId.value)) {
      selectedRowId.value = null;
    }
  },
);

// --- Bulk command issuing: multi-select players (LocationZonePlayer cards only) ---
const isLocationZonePlayer = computed(() => selectedEntityId.value === 'LocationZonePlayer');
const hasSyncPill = computed(() => showsSyncPill(selectedEntityId.value));
const selectedPlayerIds = ref<Set<string>>(new Set());

const selectedPlayerRecords = computed(() =>
  displayRows.value.filter((row: Record<string, unknown>) =>
    selectedPlayerIds.value.has(String(row.id)),
  ),
);

// Anchor for shift-click range selection (the last row clicked/toggled).
const rangeAnchorId = ref<string | null>(null);

function togglePlayerSelect(row: Record<string, unknown>) {
  const key = String(row.id);
  const next = new Set(selectedPlayerIds.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  selectedPlayerIds.value = next;
  rangeAnchorId.value = key;
}

function clearPlayerSelection() {
  selectedPlayerIds.value = new Set();
  rangeAnchorId.value = null;
}

/** Shift-click: select the contiguous range between the anchor and this row. */
function selectPlayerRange(toId: string) {
  const ids = (data.value?.data ?? []).map((row: Record<string, unknown>) => String(row.id));
  const from = rangeAnchorId.value ?? toId;
  const start = ids.indexOf(from);
  const end = ids.indexOf(toId);
  if (start === -1 || end === -1) {
    togglePlayerSelect({ id: toId });
    return;
  }
  const [lo, hi] = start <= end ? [start, end] : [end, start];
  selectedPlayerIds.value = new Set(ids.slice(lo, hi + 1));
}


// Prune checked players that scrolled out of the result set OR that live polling
// flagged as missing (deleted / de-scoped — dimmed rows must not stay commandable
// through the bulk bar); clear entirely when leaving the LocationZonePlayer entity
// (the bulk bar is LZP-only). Watches displayRows so a poll response re-runs the prune.
watch(
  [displayRows, isLocationZonePlayer],
  ([rows, isLzp]) => {
    if (!isLzp) {
      if (selectedPlayerIds.value.size > 0) clearPlayerSelection();
      return;
    }
    const present = new Set(
      (rows as Record<string, unknown>[])
        .filter((row) => !isRowMissing(row))
        .map((row) => String(row.id)),
    );
    const next = new Set([...selectedPlayerIds.value].filter((id) => present.has(id)));
    if (next.size !== selectedPlayerIds.value.size) {
      selectedPlayerIds.value = next;
    }
  },
);

// --- Bulk selection: custom lists in both the cards and the table, schedules on the cards only ---
// Players keep their own selection above. It drives the command bar, which is a different tool.
// Schedules are searched through their custom list, so the custom list is the only bulk-selectable
// entity, and it keeps a second selection for its schedules.
const BULK_SELECTABLE_ENTITY_IDS = [CUSTOM_LIST_ENTITY_ID];
const isBulkSelectable = computed(() => BULK_SELECTABLE_ENTITY_IDS.includes(selectedEntityId.value));
const { can } = usePermissions();
// A user who may list custom lists but not their schedules gets the lists alone. The schedule
// count endpoint and the schedule bulk actions would refuse them.
// The table draws no schedule checkboxes, so it offers the lists alone too.
const schedulesSelectable = computed(
  () =>
    selectedEntityId.value === CUSTOM_LIST_ENTITY_ID &&
    effectiveViewMode.value === 'cards' &&
    can('view', 'location_zone_custom_playlist_schedule'),
);

/** The ids of the rows on screen, in order. The shift-click range and the page checkbox read them. */
const pageRowIds = computed(() =>
  (displayRows.value as Record<string, unknown>[])
    .map((row) => toBulkSelectionId(row.id))
    .filter((id): id is number => id !== null),
);

// The same inputs that reset the pager, minus the page size. A new page size shows the same
// matches cut differently, so the rows the user ticked are still the rows they meant.
// Both selections share it, so a new filter, sort or entity clears the lists and the schedules.
const bulkResetKey = computed(() => JSON.stringify([entitySlug.value, columnSort.value, compositeFilter.value]));
/** Built the same way the search request builds its filter, so "all matching" names the rows on screen. */
const bulkSearchFilter = computed(() => buildSearchFilterPayload(compositeFilter.value) ?? null);
const bulkSearchInputs = () => ({ filter: bulkSearchFilter.value, sort: columnSort.value });

const bulkSelection = useBulkSelection({
  pageRowIds,
  total: cachedTotal,
  searchInputs: bulkSearchInputs,
  resetKey: bulkResetKey,
});

/**
 * The schedules each custom list on screen draws, by row id. It is worked out once per row here.
 * The card, the table cell and the shift-click range below all read it, so a row is never worked
 * out three times. It moves on with the schedule clock, like {@link cardSchedulesFor} does.
 */
const cardSchedulesByRowId = computed<ReadonlyMap<string, ScheduleEntry[]>>(() => {
  const byRowId = new Map<string, ScheduleEntry[]>();
  if (selectedEntityId.value !== CUSTOM_LIST_ENTITY_ID) return byRowId;
  for (const row of displayRows.value as Record<string, unknown>[]) {
    byRowId.set(cardSchedulesKeyOf(row), cardSchedulesFor(row));
  }
  return byRowId;
});

/**
 * The ids of the schedules on screen, in the order the cards draw them: each row's
 * card schedules, row after row. A shift-click on a schedule ranges over this order.
 */
const pageScheduleIds = computed(() =>
  schedulesSelectable.value
    ? [...cardSchedulesByRowId.value.values()]
        .flatMap((entries) => entries.map((entry) => toBulkSelectionId(entry.schedule.id)))
        .filter((id): id is number => id !== null)
    : [],
);

/** How many schedules pass the search. Asked for only once the bulk bar's top row shows. */
const matchingScheduleCount = useMatchingScheduleCount({ resetKey: bulkResetKey, filter: bulkSearchFilter });

// A separate selection from the lists', because list 5 and schedule 5 are different rows.
// Its "select all matching" sends `list_search`: the schedules that pass this custom list search.
const scheduleSelection = useBulkSelection({
  pageRowIds: pageScheduleIds,
  total: () => matchingScheduleCount.count.value ?? 0,
  searchInputs: bulkSearchInputs,
  resetKey: bulkResetKey,
  selectAllMode: 'list_search',
});

// A switch between the cards and the table clears the ticked schedules. The table cannot show
// them, so a table-view action must not act on schedules the user can no longer see. The ticked
// lists stay, because both views show a checkbox on each list.
watch(effectiveViewMode, () => scheduleSelection.clear());

/** The bar's top row shows from the first ticked list or schedule. That is when the schedules are counted. */
const bulkTopRowShows = computed(
  () => schedulesSelectable.value && (bulkSelection.hasSelection.value || scheduleSelection.hasSelection.value),
);
watch(bulkTopRowShows, (topRowShows) => {
  if (topRowShows) matchingScheduleCount.request();
});

/**
 * A bulk run ended, so it may have deleted or changed schedules that pass this search. The old
 * count would show a wrong number, and select all would send it as a stale `expected_count`.
 */
function onBulkRunFinished() {
  // Read the flag before the count is dropped. With every matching schedule selected, the
  // selection's size comes from the count, so dropping the count makes the flag read false.
  const topRowShowed = bulkTopRowShows.value;
  matchingScheduleCount.invalidate();
  if (topRowShowed) matchingScheduleCount.request();
}

/**
 * The ticked schedules among those on screen, for the checkboxes. It covers "all matching" too.
 *
 * It is one Set for the life of the page, changed in place. Every card gets this same Set, so a
 * tick on schedule 9001 redraws only the line that asks about 9001. A new Set on each tick would
 * redraw every schedule line on the page.
 */
const selectedScheduleIds = reactive(new Set<number>());
const selectedScheduleIdsOnPage = computed(() => pageScheduleIds.value.filter((id) => scheduleSelection.isSelected(id)));
watch(
  selectedScheduleIdsOnPage,
  (tickedIds) => {
    const ticked = new Set(tickedIds);
    for (const id of [...toRaw(selectedScheduleIds)]) {
      if (!ticked.has(id)) selectedScheduleIds.delete(id);
    }
    for (const id of ticked) {
      if (!toRaw(selectedScheduleIds).has(id)) selectedScheduleIds.add(id);
    }
  },
  { immediate: true, flush: 'sync' },
);

function onToggleScheduleSelect(scheduleId: number, event: MouseEvent) {
  scheduleSelection.toggle(scheduleId, event);
}

// The cards, the table's Schedules cell and the inspector read each row's schedules from here.
// The table cell only shows schedules. It never ticks them, so it needs nothing else.
provideCardSchedules(cardSchedulesByRowId);

/** What the bulk bar needs of the schedule selection. Null on any entity without one. */
const scheduleBarSelection = computed<BulkBarScheduleSelection | null>(() =>
  schedulesSelectable.value
    ? {
        mode: scheduleSelection.mode.value,
        count: scheduleSelection.count.value,
        excludedCount: scheduleSelection.excludedIds.value.size,
        total: matchingScheduleCount.count.value,
        totalFailed: matchingScheduleCount.isError.value,
        canSelectAllMatching: matchingScheduleCount.count.value !== null && scheduleSelection.hasUntickedMatches.value,
      }
    : null,
);

/**
 * "Select all matching N" beside the count. On the custom list search the offer names lists and
 * schedules, and shows after a ticked schedule too, so it asks only whether more lists match.
 */
const canSelectAllMatchingLists = computed(() =>
  schedulesSelectable.value ? bulkSelection.hasUntickedMatches.value : bulkSelection.canSelectAllMatching.value,
);

/**
 * The bar's clear. With no kinds named, as from its clear button, both selections are cleared.
 * After an action starts it names the kinds the action covered. Add tag, for example, covers the
 * lists only, so the ticked schedules stay ticked.
 */
function clearBulkSelections(kinds?: MixedRunKind[]) {
  if (!kinds || kinds.includes('lists')) bulkSelection.clear();
  if (!kinds || kinds.includes('schedules')) scheduleSelection.clear();
}

/** A card's tint: the bulk selection for schedules and lists, the player selection for players. */
function isCardSelected(row: Record<string, unknown>): boolean {
  return isBulkSelectable.value
    ? bulkSelection.isSelected(row.id)
    : selectedPlayerIds.value.has(String(row.id));
}

function onToggleBulkSelect(row: Record<string, unknown>, event: MouseEvent) {
  bulkSelection.toggle(row.id, event);
}

function isTableRowSelected(row: Record<string, unknown>): boolean {
  return bulkSelection.isSelected(row.id);
}

/**
 * Split view renders its detail inline, so close any stale overlay inspector
 * when it becomes active (e.g. one carried over from another page); leaving
 * split view just drops the inline selection.
 */
watch(
  isSplitViewActive,
  (active) => {
    if (active) {
      if (inspectorStore.isOpen) {
        inspectorStore.close();
      }
    } else {
      selectedRowId.value = null;
    }
  },
  { immediate: true },
);

// Leaving the table's record pane, by switching to cards or map, or by narrowing
// to a phone, drops its selection.
watch(isTablePaneActive, (active) => {
  if (!active && !isSplitViewActive.value) selectedRowId.value = null;
});
watch(effectiveViewMode, () => {
  selectedRowId.value = null;
});

/** The table's selected row id, for its highlight. Only while the record shows below it. */
const tableSelectedRowId = computed(() => (isTablePaneActive.value ? selectedRowId.value : null));

/**
 * ↓ and ↑ move the table's selection and the record below follows. Escape clears
 * it. See tableSelectionKeys for when the list leaves a key alone.
 */
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (!hasRecordPane.value || selectedRowId.value === null) return;
  if (!listMayHandleKey(event, inspectorStore.isOpen)) return;
  // Escape steps back one record along the pane's trail, in a table or in cards.
  if (event.key === 'Escape' && paneTrail.back()) {
    event.preventDefault();
    return;
  }
  // The rest are the table's. Cards keep their own keys.
  if (!isTablePaneActive.value) return;
  const rowIds = (displayRows.value as Record<string, unknown>[]).map((row) => String(row.id));
  const action = tableSelectionKeyAction(event.key, rowIds, selectedRowId.value);
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') event.preventDefault();
  if (action === null) return;
  event.preventDefault();
  if (action.kind === 'clear') {
    selectedRowId.value = null;
    return;
  }
  selectedRowId.value = action.rowId;
  void nextTick(() => {
    document
      .querySelector('[data-testid="search-table"] [data-active-row="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  });
});

function onRowSelect(row: Record<string, unknown>, event?: MouseEvent) {
  const id = String(row.id);

  // Multi-select gestures on the player cards (mirror Finder/Linear): shift
  // extends a range and cmd/ctrl toggles — both adjust the selection without
  // opening the inspector. A plain click selects only this row AND opens it.
  if (isLocationZonePlayer.value) {
    if (event?.shiftKey && rangeAnchorId.value) {
      selectPlayerRange(id);
      return;
    }
    if (event?.metaKey || event?.ctrlKey) {
      togglePlayerSelect(row);
      return;
    }
    selectedPlayerIds.value = new Set([id]);
    rangeAnchorId.value = id;
  }

  const scheduleListId = scheduleListIdFor(row);
  if (scheduleListId !== null) requestScheduleInList(row, scheduleListId);

  if (isSplitViewActive.value) {
    selectedRowId.value = id;
    paneTrail.clear();
    return;
  }
  // A schedule has no page of its own: it is read in its list, so its list
  // opens in the peek, on Schedules, with the schedule highlighted.
  if (scheduleListId !== null) {
    openRecord(CUSTOM_LIST_ENTITY_ID, scheduleListId, { initialSection: 'schedules' });
    return;
  }
  // On a phone or tablet a plain tap opens the record's full page, as a table
  // row click does. Off a list route, as in a bare test mount, it peeks.
  if (listRouter) {
    openRecordPage(row);
  } else {
    openRecord(selectedEntityId.value, row.id as number | string);
  }
}

// Esc clears the player multi-selection. Bound to the cards container (below),
// not window, so an Escape closing an open popover/menu/select — whose focus
// lives in a body-level portal outside this subtree — doesn't also wipe the
// selection; only an Escape with focus in the card list clears it.
function onCardsKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && selectedPlayerIds.value.size > 0) {
    clearPlayerSelection();
  }
}

const columnsResizeHandler = (columnId: string, width: number) => {
  setColumnSize(columnId, width);
};

const columnsSortHandler = (columnField: string) => {
  toggleSort(columnField);
};

const columnsReorderHandler = (columns: TableColumn[]) => {
  updateColumnState(columns)
};
</script>

<template>
  <!-- Zero permitted models (SAU-287): no picker, no dead table — say it plainly. -->
  <div
    v-if="hasNoPermittedEntities"
    class="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-1 p-8 text-center"
    data-testid="search-no-entities"
  >
    <p class="text-sm font-medium">No models available</p>
    <p class="text-muted-foreground text-sm">
      Your account doesn't have view access to any searchable models yet. Ask an administrator to grant access.
    </p>
  </div>
  <!--
    The page is one flat ground, as in Macro. The toolbar, the table and (on
    mobile) the card list all sit on it with no box of their own, so nothing
    reads as a separate sheet.
  -->
  <div v-else class="flex min-h-0 min-w-0 flex-1 flex-col bg-background">
    <!-- Toolbar (entity, filters, paginator, settings): stays visible above the
         scroll area. No background of its own — the ground shows through. -->
    <div ref="toolbarRef" class="z-30 shrink-0">
      <EditableSearchTableHeader
        :totalRecords="cachedTotal"
        :current-page="pageNumber"
        :has-next="hasNext"
        :has-prev="hasPrev"
        :loading="isLoading"
        :has-sync-pill="hasSyncPill"
        @navigate="navigateHandler"
      >
        <!-- The Sync pill rides with the live-polling indicator, on the lists
             showsSyncPill names. The header places the
             cluster (see searchToolbarStatusLayout). The indicator comes after the
             pill, so it sits between the pill and the pager. -->
        <template #status="{ form }">
          <div class="flex items-center gap-2">
            <SyncStatusButton v-if="hasSyncPill" :form="form" />
            <SearchLivePollingIndicator
              :state="livePollState"
              :payload-bytes="livePollPayloadBytes"
              :max-payload-bytes="livePollMaxPayloadBytes"
              :poll-interval-ms="livePollIntervalMs"
              :last-updated-at="livePollLastUpdatedAt ?? 0"
            />
          </div>
        </template>
        <!-- Export the result set on screen to a spreadsheet, whichever entity
             is being searched. Export opens the export column list, which
             starts from the columns on screen. The control renders nothing
             without the export permission. -->
        <template v-if="searchSource" #actions="{ fit }">
          <TrackListExportControls
            :source="searchSource"
            :label="searchLabel"
            :columns="exportColumnIds"
            :column-options="exportColumnOptions"
            :fit="fit"
            button-variant="toolbar"
          />
        </template>
      </EditableSearchTableHeader>
    </div>
    <!-- Explicit error variant: a failed search (e.g. a 403 on a model this
         user lost access to) must never masquerade as an empty result. It sits
         above the view branches so the table and card views say it too — an
         empty table reads as "nothing matched", which is the silent failure
         this exists to prevent. -->
    <p
      v-if="isSearchError && !isLoading"
      class="text-destructive px-4 pt-6 text-center text-sm"
      data-testid="search-error-state"
    >
      Couldn't load results. You may not have access to this model — pick another, or retry.
    </p>
    <!--
      The bulk actions for the selected lists and schedules. It sits above the view branches, so the
      cards and the table both show it. It stays mounted on these entities even with nothing
      selected, because it also follows a running bulk action and shows its report.
    -->
    <BulkActionBar
      v-if="isBulkSelectable && entitySlug"
      class="shrink-0"
      :entity-slug="entitySlug"
      :mode="bulkSelection.mode.value"
      :count="bulkSelection.count.value"
      :excluded-count="bulkSelection.excludedIds.value.size"
      :total="cachedTotal"
      :can-select-all-matching="canSelectAllMatchingLists"
      :loading="isLoading"
      :build-selection="bulkSelection.buildSelectionPayload"
      :schedules="scheduleBarSelection"
      :build-schedule-selection="schedulesSelectable ? scheduleSelection.buildSelectionPayload : null"
      @clear="clearBulkSelections"
      @select-all-matching="bulkSelection.selectAllMatching()"
      @select-all-matching-schedules="scheduleSelection.selectAllMatching()"
      @run-finished="onBulkRunFinished"
    />
    <!--
      Results surface: a white sheet on the ground with an even 12px gap on all
      four sides, square-cornered and hairline-bordered — the rounded corners in
      this view belong to the row cards inside it. It is also the scroll
      container, which is what `table-in-parent-scroll` + `sticky-header` need
      for a thead that sticks to the top of the results rather than the page.
    -->
    <div v-if="effectiveViewMode === 'list'" class="min-h-0 w-full flex-1 p-3">
      <TableRecordSplitView :has-selection="isTablePaneActive && selectedRow !== null">
        <template #table>
        <!-- The table's schedule cells order each row's lines by the sort the rows came back
             with, not the live sort. The record pane sits outside, so it keeps the card order. -->
        <TableScheduleSortScope :sort="data?.sentSort">
        <DataTable
          :key="entitySlug"
          data-testid="search-table"
          table-in-parent-scroll
          :data="displayRows"
          :group-by-field="groupBy"
          :is-row-missing="isRowMissing"
          :totalRecords="cachedTotal"
          :columns="unpinnedColumns.filter((column) => !column.cardOnly)"
          :pinnedColumns="pinnedColumns.filter((column) => !column.cardOnly)"
          :isLoading="isLoading"
          :pageIndex="pageIndex"
          :pageSize="pageSize"
          sticky-header
          :selectable="isBulkSelectable"
          :is-row-selected="isTableRowSelected"
          :page-selection-state="bulkSelection.pageSelectionState.value"
          @toggle-page="bulkSelection.togglePage()"
          @toggle-row="onToggleBulkSelect"
          row-id-key="id"
          :selected-row-id="tableSelectedRowId"
          :on-row-click="onEntityListRoute ? onTableRowClick : undefined"
          @columnsSorted="columnsSortHandler"
          @columnsResized="columnsResizeHandler"
          @columnsReordered="columnsReorderHandler"
        >
          <template #cell="{ column, row }">
            <div v-if="column.id === 'play'" class="px-2 py-1">
              <PlayTrackButton v-bind="playButtonPropsForRow(row)" />
            </div>
            <ListRowPaneScope v-else :row-id="String(row.id)">
              <SearchHighlight
                :name="LIST_HIGHLIGHT"
                :terms="quickSearchMatches.terms.value"
                :active="cellMatched(row, column.key)"
                :whole="quickSearchMatches.isNumberField(column.key)"
              >
                <ColumnMapping
                  :field="column.key"
                  :dataType="column.dataType"
                  :data="row"
                />
              </SearchHighlight>
              <p
                v-if="column.id === firstTableColumnId && tableHiddenMatch(row)"
                class="truncate px-2 pb-1 text-xs text-muted-foreground"
                data-testid="list-search-hidden-match"
              >
                <SearchHighlight
                  :name="LIST_HIGHLIGHT"
                  :terms="quickSearchMatches.terms.value"
                  :whole="quickSearchMatches.isNumberField(tableHiddenMatch(row)!.field)"
                >{{ tableHiddenMatch(row)!.value }}</SearchHighlight>
              </p>
            </ListRowPaneScope>
          </template>
        </DataTable>
        </TableScheduleSortScope>
        </template>
        <template #record>
          <RecordPane
            v-if="selectedRow"
            :entity-id="selectedEntityId"
            :row="selectedRow"
            :schedule-list-id="selectedScheduleListId"
            :use-layout="onEntityListRoute"
            :inspector-columns="inspectorColumns"
            :trail="paneTrail"
            closable
            escape-closes
            @open="openRecordPage(selectedRow)"
            @close="selectedRowId = null"
          />
        </template>
      </TableRecordSplitView>
    </div>

    <!-- `relative`: on mobile this is the card list's scroll container, and a scroll
         container that is not positioned does not clip absolutely positioned
         descendants (an `sr-only` label is one). They belong to the layout's content
         region instead, and the ones in cards scrolled out of view sat below the
         viewport and made the whole page scroll. Desktop is covered by the split
         view's own panes, which are positioned for the same reason. -->
    <div
      v-else-if="effectiveViewMode === 'cards'"
      class="relative flex min-h-0 w-full flex-1 flex-col p-3 max-md:pb-mobile-area-bar-clearance"
      :class="isSplitViewActive ? 'overflow-hidden' : 'overflow-auto'"
      @keydown="onCardsKeydown"
    >
      <!-- Bulk command-issue bar for the checked players (LocationZonePlayer only). -->
      <LzpBulkIssueBar
        v-if="isLocationZonePlayer && selectedPlayerIds.size > 0"
        :players="selectedPlayerRecords"
        class="shrink-0"
        @clear="clearPlayerSelection"
      />
      <!--
        Desktop card mode: Apple Mail-style master-detail. Compact rows in the
        left pane, the entity's inspector rendered inline on the right.
      -->
      <SearchCardsSplitView
        v-if="isSplitViewActive"
        class="bg-background border-border/70 min-h-0 flex-1 overflow-hidden border"
        :rows="displayRows"
        :group-headers="cardGroupHeaders"
        :has-selection="selectedRow !== null"
        :is-loading="isLoading"
      >
        <template #row="{ row }">
          <ListRowPaneScope :row-id="String(row.id)">
          <SearchResultListRow
            :row="row"
            :title-icon="isContainerCard ? null : titleIconForRow(row)"
            :visible-columns="fixedCardComponent ? [] : cardColumns"
            :exclude-fields="rowExcludeFields"
            :active="String(row.id) === selectedRowId"
            :selected="isCardSelected(row)"
            :selectable="isBulkSelectable"
            :missing="isRowMissing(row)"
            :shown-field-keys="cardShownFieldKeys(row)"
            @select="onRowSelect"
            @toggle-select="onToggleBulkSelect"
            @dblclick="openRecordPage(row)"
          >
            <template v-if="!fixedCardComponent && showCoverThumb" #leading>
              <div class="float-left mr-2.5 mb-1">
                <CoverArtTile
                  :src="(row.cover_art_signed_url as string | null) ?? null"
                  :alt="coverThumbAlt(row)"
                  :size="44"
                  :play="playButtonPropsForRow(row)"
                />
              </div>
            </template>
            <template v-if="fixedCardComponent" #header>
              <component :is="fixedCardComponent" :row="row" :matched-fields="cardMatchedFields(row)" />
            </template>
            <template v-else-if="selectedEntityId === 'LocationZonePlayer'" #header>
              <LocationZonePlayerCardHeader :row="row" :matched-fields="cardMatchedFields(row)" />
            </template>
            <template v-else-if="isContainerCard" #header>
              <PlaylistContainerCardHeader
                :row="row"
                :entity-id="selectedEntityId"
                :schedules-selectable="schedulesSelectable"
                :selected-schedule-ids="selectedScheduleIds"
                :matched-fields="cardMatchedFields(row)"
                @toggle-schedule="onToggleScheduleSelect"
              />
            </template>
            <template v-if="currentlyPlayingVisible" #subheader>
              <LzpCurrentlyPlayingCard
                :row="row"
                :track="currentTrackSummaryForRow(row)"
                :matched-fields="cardMatchedFields(row)"
              />
            </template>
            <template v-if="showCardTags || showLiveTrackRating" #footer>
              <CardTags
                :tags="showCardTags ? row.tags : undefined"
                :show-rating="showLiveTrackRating"
                :rating="row.rating"
                :matched-fields="cardMatchedFields(row)"
              />
            </template>
          </SearchResultListRow>
          </ListRowPaneScope>
        </template>
        <template #detail>
          <RecordPane
            v-if="selectedRow"
            :entity-id="selectedEntityId"
            :row="selectedRow"
            :schedule-list-id="selectedScheduleListId"
            :use-layout="onEntityListRoute"
            :inspector-columns="inspectorColumns"
            :trail="paneTrail"
            closable
            @open="openRecordPage(selectedRow)"
            @close="selectedRowId = null"
          />
        </template>
      </SearchCardsSplitView>
      <!-- Phone and tablet card mode: list only; tapping a row opens its full page. -->
      <div v-else data-testid="search-card-list" class="flex w-full max-w-3xl flex-col gap-2 pt-2">
        <template v-for="(row, index) in displayRows" :key="String(row.id)">
        <ListGroupHeader v-if="cardGroupHeaders.has(index)" :label="cardGroupHeaders.get(index)!" />
        <SearchResultListRow
          :row="row"
          :title-icon="isContainerCard ? null : titleIconForRow(row)"
          :visible-columns="fixedCardComponent ? [] : cardColumns"
          :exclude-fields="rowExcludeFields"
          :selected="isCardSelected(row)"
          :selectable="isBulkSelectable"
          :missing="isRowMissing(row)"
          :shown-field-keys="cardShownFieldKeys(row)"
          @select="onRowSelect"
          @toggle-select="onToggleBulkSelect"
        >
          <template v-if="!fixedCardComponent && showCoverThumb" #leading>
            <div class="float-left mr-2.5 mb-1">
              <CoverArtTile
                :src="(row.cover_art_signed_url as string | null) ?? null"
                :alt="coverThumbAlt(row)"
                :size="44"
                :play="playButtonPropsForRow(row)"
              />
            </div>
          </template>
          <template v-if="fixedCardComponent" #header>
            <component :is="fixedCardComponent" :row="row" :matched-fields="cardMatchedFields(row)" />
          </template>
          <template v-else-if="selectedEntityId === 'LocationZonePlayer'" #header>
            <LocationZonePlayerCardHeader :row="row" :matched-fields="cardMatchedFields(row)" />
          </template>
          <template v-else-if="isContainerCard" #header>
            <PlaylistContainerCardHeader
              :row="row"
              :entity-id="selectedEntityId"
              :schedules-selectable="schedulesSelectable"
              :selected-schedule-ids="selectedScheduleIds"
              :matched-fields="cardMatchedFields(row)"
              @toggle-schedule="onToggleScheduleSelect"
            />
          </template>
          <template v-if="currentlyPlayingVisible" #subheader>
            <LzpCurrentlyPlayingCard
              :row="row"
              :track="currentTrackSummaryForRow(row)"
              :matched-fields="cardMatchedFields(row)"
            />
          </template>
          <template v-if="showCardTags || showLiveTrackRating" #footer>
            <CardTags
              :tags="showCardTags ? row.tags : undefined"
              :show-rating="showLiveTrackRating"
              :rating="row.rating"
              :matched-fields="cardMatchedFields(row)"
            />
          </template>
        </SearchResultListRow>
        </template>
        <!-- Suppressed while the shared error banner above is showing, so a
             failed search never also reads as "No results." -->
        <p
          v-if="!isSearchError && (data?.data?.length ?? 0) === 0 && !isLoading"
          class="text-muted-foreground pt-6 text-center text-sm"
        >
          No results.
        </p>
      </div>
    </div>

    <div
      v-if="canUseMapView && mapHostReady"
      v-show="effectiveViewMode === 'map'"
      class="flex min-h-0 min-w-0 flex-1 flex-col p-3"
    >
      <!-- Same sheet as the list and cards views: the map is a results surface
           too, so it gets the even gutter and the hairline edge. -->
      <div class="bg-background border-border/70 flex min-h-0 flex-1 flex-col overflow-hidden border">
        <SearchMapView :rows="displayRows" :is-loading="isLoading" />
      </div>
    </div>
  </div>
</template>
